import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Session } from '@supabase/supabase-js';
import type {
  User,
  Organization,
  UserOrganization,
  Member,
  Meeting,
  DuePayment,
  DonationEvent,
  Donation,
  FinancialTransaction
} from '@/types';

export type UserRole = 'admin' | 'treasurer' | 'secretary' | 'member';

export type SyncStatus = 'synced' | 'syncing' | 'pending' | 'error';

interface AppState {
  // Auth State
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  // Starts as false — App.tsx's initAuth() sets it to true immediately on
  // mount, so there is no stale-true window. Previously initialised to true,
  // which caused every route guard / spinner to stall for up to 4 s on page
  // refresh even though Zustand had already rehydrated isAuthenticated from
  // localStorage synchronously.
  isLoading: boolean;

  // Organization State
  currentOrganization: Organization | null;
  organizations: UserOrganization[];
  userRole: UserRole | null;
  // Tracks whether the initial org fetch has completed for this session.
  // Kept in the store (not MainLayout local state) so it survives remounts
  // when the user navigates between public and protected routes — prevents
  // the org-loading spinner from flashing and navigate() from re-firing.
  orgFetched: boolean;

  // Data State
  members: Member[];
  meetings: Meeting[];
  dues: DuePayment[];
  donationEvents: DonationEvent[];
  donations: Donation[];
  transactions: FinancialTransaction[];

  // UI State
  sidebarOpen: boolean;
  language: string;
  isOffline: boolean;
  syncStatus: SyncStatus;

  // Actions
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;

  setCurrentOrganization: (organization: Organization | null, role?: UserRole) => void;
  setOrganizations: (organizations: UserOrganization[]) => void;
  setUserRole: (role: UserRole | null) => void;
  setOrgFetched: (fetched: boolean) => void;
  addOrganization: (org: UserOrganization) => void;

  // Members Actions
  setMembers: (members: Member[]) => void;
  addMember: (member: Member) => void;
  updateMember: (member: Member) => void;
  removeMember: (memberId: string) => void;

  // Meetings Actions
  setMeetings: (meetings: Meeting[]) => void;
  addMeeting: (meeting: Meeting) => void;

  // Dues Actions
  setDues: (dues: DuePayment[]) => void;
  addPayment: (payment: DuePayment) => void;

  // Donations Actions
  setDonationEvents: (events: DonationEvent[]) => void;
  addDonationEvent: (event: DonationEvent) => void;
  setDonations: (donations: Donation[]) => void;
  addDonation: (donation: Donation) => void;

  // Finance Actions
  setTransactions: (transactions: FinancialTransaction[]) => void;
  addExpense: (transaction: FinancialTransaction) => void;
  addIncome: (transaction: FinancialTransaction) => void;

  // UI Actions
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setLanguage: (language: string) => void;
  setIsOffline: (isOffline: boolean) => void;
  setSyncStatus: (status: SyncStatus) => void;

  logout: () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Initial State
      user: null,
      session: null,
      isAuthenticated: false,
      // false — App.tsx sets this to true at the start of initAuth().
      // Starting at true caused a 4 s stall on every refresh because Zustand
      // rehydrates persisted state (isAuthenticated, user, etc.) synchronously
      // but isLoading was never persisted, so it always booted as true.
      isLoading: false,

      currentOrganization: null,
      organizations: [],
      userRole: null,
      orgFetched: false,

      members: [],
      meetings: [],
      dues: [],
      donationEvents: [],
      donations: [],
      transactions: [],

      sidebarOpen: true,
      language: 'en',
      isOffline: false,
      syncStatus: 'synced',

      // Auth Actions
      setUser: (user) => set({ user }),
      setSession: (session) => set({ session }),
      setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
      setIsLoading: (isLoading) => set({ isLoading }),

      // Organization Actions
      setCurrentOrganization: (organization, role) =>
        set({
          currentOrganization: organization,
          userRole: role || null,
        }),
      setOrganizations: (organizations) => set({ organizations }),
      setUserRole: (role) => set({ userRole: role }),
      setOrgFetched: (fetched) => set({ orgFetched: fetched }),
      addOrganization: (org) =>
        set((state) => ({
          organizations: [...state.organizations, org],
        })),

      // Members Actions
      setMembers: (members) => set({ members }),
      addMember: (member) =>
        set((state) => ({
          members: [...state.members, member],
        })),
      updateMember: (member) =>
        set((state) => ({
          members: state.members.map((m) => (m.id === member.id ? member : m)),
        })),
      removeMember: (memberId) =>
        set((state) => ({
          members: state.members.filter((m) => m.id !== memberId),
        })),

      // Meetings Actions
      setMeetings: (meetings) => set({ meetings }),
      addMeeting: (meeting) =>
        set((state) => ({
          meetings: [...state.meetings, meeting],
        })),

      // Dues Actions
      setDues: (dues) => set({ dues }),
      addPayment: (payment) =>
        set((state) => ({
          dues: [...state.dues, payment],
        })),

      // Donations Actions
      setDonationEvents: (events) => set({ donationEvents: events }),
      addDonationEvent: (event) =>
        set((state) => ({
          donationEvents: [...state.donationEvents, event],
        })),
      setDonations: (donations) => set({ donations }),
      addDonation: (donation) =>
        set((state) => ({
          donations: [...state.donations, donation],
        })),

      // Finance Actions
      setTransactions: (transactions) => set({ transactions }),
      addExpense: (transaction) =>
        set((state) => ({
          transactions: [...state.transactions, transaction],
        })),
      addIncome: (transaction) =>
        set((state) => ({
          transactions: [...state.transactions, transaction],
        })),

      // UI Actions
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setLanguage: (language) => set({ language }),
      setIsOffline: (isOffline) => set({ isOffline }),
      setSyncStatus: (syncStatus) => set({ syncStatus }),

      logout: () =>
        set({
          user: null,
          session: null,
          isAuthenticated: false,
          currentOrganization: null,
          organizations: [],
          userRole: null,
          // Reset org fetch flag so the next login re-fetches organisations
          // from scratch rather than skipping the fetch entirely.
          orgFetched: false,
          members: [],
          meetings: [],
          dues: [],
          donationEvents: [],
          donations: [],
          transactions: [],
        }),
    }),
    {
      name: 'tickally-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        currentOrganization: state.currentOrganization,
        userRole: state.userRole,
        language: state.language,
        // orgFetched is intentionally NOT persisted — it should reset to false
        // on every fresh browser session so organisations are always re-validated
        // against the server on load.
      }),
    }
  )
);
